import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eye, EyeSlash, Lock } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "admin" ? "/admin" : "/learn");
    } catch (err) {
      const msg = apiError(err.response?.data?.detail) || "Login failed";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-primary p-12 overflow-hidden">
        <div className="absolute inset-0 noise-overlay opacity-5 pointer-events-none" />
        <div className="flex items-center gap-3 relative z-10">
          <div className="bg-white rounded-xl px-3 py-2 shadow-sm">
            <img src="/mcr-logo.png" alt="Mobile Crisis 24/7 Services" className="h-8 w-auto" />
          </div>
          <div className="leading-tight">
            <p className="text-white/70 text-sm">Cyber Security Training</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="font-heading font-black text-white text-4xl lg:text-5xl tracking-tight leading-[1.05]">
            Stay sharp. <br />Spot the scam.
          </h1>
          <p className="text-white/70 mt-6 leading-relaxed">
            Phishing emails try to trick us into giving away passwords and information. This short
            program teaches you how to recognise them and protects the community we serve, 24/7.
          </p>
        </div>

        <p className="relative z-10 text-white/40 text-xs">Serving Regina & area for over 50 years.</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/mcr-logo.png" alt="Mobile Crisis 24/7 Services" className="h-9 w-auto" />
          </div>

          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Secure sign in</p>
          <h2 className="font-heading font-bold text-3xl text-foreground tracking-tight mt-2">Welcome back</h2>
          <p className="text-muted-foreground mt-2 text-sm">
            Use the account credentials provided by your administrator.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="you@mcsregina.ca"
                className="w-full h-12 px-4 rounded-lg border border-input bg-white text-foreground outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors duration-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  placeholder="Your password"
                  className="w-full h-12 px-4 pr-12 rounded-lg border border-input bg-white text-foreground outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors duration-200"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors duration-200"
                  data-testid="toggle-password"
                >
                  {show ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full h-12 rounded-full bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 hover:-translate-y-0.5 transition-transform duration-200 disabled:opacity-60"
            >
              <Lock size={18} weight="duotone" />
              {busy ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <p className="text-xs text-muted-foreground mt-6 text-center">
            Don't have an account? Contact your administrator to have one created.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
